import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { jobArchitectureKeys } from '../../api/query-keys';

type ItemDetails = JobRoleDetails | JobLevelDetails;

export type DetailsSnapshot = readonly [QueryKey, ItemDetails | undefined][];

const versionString = (version: number): string => version.toString();

export const itemDetailsCache = {
    cancel: async (
        queryClient: QueryClient,
        architectureId: string,
        itemId: string,
        version: number,
        includeComments: boolean,
    ): Promise<void> => {
        const versionKey = versionString(version);
        await Promise.all([
            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.role(architectureId, itemId, versionKey, includeComments),
            }),
            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.level(architectureId, itemId, versionKey, includeComments),
            }),
        ]);
    },
    invalidate: (
        queryClient: QueryClient,
        architectureId: string,
        itemId: string,
        version: number,
        includeComments: boolean,
    ): void => {
        const versionKey = versionString(version);
        queryClient.invalidateQueries({
            queryKey: jobArchitectureKeys.role(architectureId, itemId, versionKey, includeComments),
        });
        queryClient.invalidateQueries({
            queryKey: jobArchitectureKeys.level(architectureId, itemId, versionKey, includeComments),
        });
    },
    overlay: (
        queryClient: QueryClient,
        architectureId: string,
        itemId: string,
        version: number,
        includeComments: boolean,
        updater: (prev: ItemDetails | undefined) => ItemDetails | undefined,
    ): void => {
        const versionKey = versionString(version);
        queryClient.setQueriesData<ItemDetails>(
            { queryKey: jobArchitectureKeys.role(architectureId, itemId, versionKey, includeComments) },
            updater,
        );
        queryClient.setQueriesData<ItemDetails>(
            { queryKey: jobArchitectureKeys.level(architectureId, itemId, versionKey, includeComments) },
            updater,
        );
    },
    restore: (queryClient: QueryClient, snapshot: DetailsSnapshot): void => {
        for (const [queryKey, data] of snapshot) {
            queryClient.setQueryData(queryKey, data);
        }
    },
    snapshot: (
        queryClient: QueryClient,
        architectureId: string,
        itemId: string,
        version: number,
        includeComments: boolean,
    ): DetailsSnapshot => {
        const versionKey = versionString(version);
        const roleEntries = queryClient.getQueriesData<ItemDetails>({
            queryKey: jobArchitectureKeys.role(architectureId, itemId, versionKey, includeComments),
        });
        const levelEntries = queryClient.getQueriesData<ItemDetails>({
            queryKey: jobArchitectureKeys.level(architectureId, itemId, versionKey, includeComments),
        });
        return [...roleEntries, ...levelEntries];
    },
};
